<template>
  <div class="card card-custom gutter-b" v-loading="loading">
    <InvoiceFormModal
      :visible="invoiceModalVisible"
      :mode="invoiceModalMode"
      :initial-data="editingInvoice"
      :product-options="needPurchaseProductOptions"
      :product-options-loading="productOptionsLoading"
      :submit-handler="handleInvoiceSubmit"
      @close="invoiceModalVisible = false"
      @submit="onInvoiceSubmitSuccess"
    />
    <InvoicePaymentStatusModal
      :visible="paymentStatusModalVisible"
      :invoice="paymentStatusInvoice"
      :submit-handler="handlePaymentStatusSubmit"
      @close="paymentStatusModalVisible = false"
      @submit="onPaymentStatusSubmitSuccess"
    />

    <div class="card-header d-flex justify-content-between">
      <div class="card-title">
        <h3 class="card-label">Danh sách hóa đơn</h3>
      </div>
      <div class="card-title">
        <button
          type="button"
          class="btn btn-light-primary font-weight-bolder mr-2"
          :disabled="exporting"
          @click="handleExport"
        >
          <span class="svg-icon svg-icon-md"><i class="el-icon-download"></i></span>
          {{ exporting ? "Đang xuất..." : "Export" }}
        </button>
        <button class="btn btn-primary font-weight-bold" @click="handleAddNew">
          <i class="el-icon-plus"></i> Thêm mới
        </button>
      </div>
    </div>

    <div class="card-body">
      <div class="row filter-form">
        <div class="col-xl-2 col-md-4">
          <div class="form-group">
            <el-input
              v-model="filterQuery.invoice_code"
              clearable
              placeholder="Mã hóa đơn"
              @input="handleFilterInput"
              @clear="handleFilter"
            />
          </div>
        </div>

        <div class="col-xl-2 col-md-4">
          <div class="form-group">
            <el-input
              v-model="filterQuery.pnk_code"
              clearable
              placeholder="Mã PNK"
              @input="handleFilterInput"
              @clear="handleFilter"
            />
          </div>
        </div>

        <div class="col-xl-2 col-md-4">
          <div class="form-group">
            <el-input
              v-model="filterQuery.product_name"
              clearable
              placeholder="Sản phẩm"
              @input="handleFilterInput"
              @clear="handleFilter"
            />
          </div>
        </div>

        <div class="col-xl-2 col-md-4">
          <div class="form-group">
            <el-date-picker
              v-model="filterQuery.purchase_date"
              class="w-100"
              type="date"
              clearable
              format="dd-MM-yyyy"
              value-format="yyyy-MM-dd"
              placeholder="Ngày mua"
              @change="handleFilter"
            />
          </div>
        </div>

        <div class="col-xl-2 col-md-4">
          <div class="form-group">
            <el-date-picker
              v-model="filterQuery.created_date"
              class="w-100"
              type="date"
              clearable
              format="dd-MM-yyyy"
              value-format="yyyy-MM-dd"
              placeholder="Ngày tạo"
              @change="handleFilter"
            />
          </div>
        </div>

        <div class="col-xl-2 col-md-4">
          <div class="form-group">
            <el-select
              v-model="filterQuery.payment_status"
              class="w-100"
              clearable
              placeholder="Trạng thái thanh toán"
              @change="handleFilter"
            >
              <el-option
                v-for="option in paymentStatusOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>
        </div>

        <div class="col-xl-2 col-md-4">
          <div class="form-group">
            <el-select
              v-model="filterQuery.import_status"
              class="w-100"
              clearable
              placeholder="Trạng thái nhập kho"
              @change="handleFilter"
            >
              <el-option
                v-for="option in importStatusOptions"
                :key="option.value"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </div>
        </div>
      </div>

      <div class="table-responsive">
        <table class="table table-vertical-center table-bordered mb-0">
          <thead class="bg-light">
            <tr>
              <th class="text-center align-middle table-stt">STT</th>
              <th class="text-left align-middle">Mã hóa đơn</th>
              <th class="text-left align-middle">Mã PNK</th>
              <th class="text-left align-middle">Sản phẩm</th>
              <th class="text-left align-middle">Tổng tiền</th>
              <th class="text-center align-middle">Ngày mua</th>
              <th class="text-center align-middle">Ngày tạo</th>
              <th class="text-center align-middle">Trạng thái thanh toán</th>
              <th class="text-center align-middle">Trạng thái nhập kho</th>
              <th class="text-center align-middle table-actions">Hành động</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="invoiceList.length > 0">
              <tr v-for="(item, index) in invoiceList" :key="item.id">
                <td class="text-center align-middle">
                  {{ (page - 1) * 20 + index + 1 }}
                </td>
                <td class="text-left align-middle">{{ item.invoice_code }}</td>
                <td class="text-left align-middle">{{ item.pnk_code || "-" }}</td>
                <td class="text-left align-middle">
                  <p class="product-names line-clamp-1 mb-0" :title="formatProductNames(item.products)">
                    {{ formatProductNames(item.products) }}
                  </p>
                </td>
                <td class="text-left align-middle">{{ formatCurrency(item.total_amount) }}</td>
                <td class="text-center align-middle">{{ formatDate(item.purchase_date) }}</td>
                <td class="text-center align-middle">{{ formatDate(item.created_at) }}</td>
                <td class="text-center align-middle">
                  <span :class="paymentStatusBadgeClass(item.payment_status)">
                    {{ paymentStatusLabel(item.payment_status) }}
                  </span>
                </td>
                <td class="text-center align-middle">
                  <span :class="importStatusBadgeClass(item.import_status)">
                    {{ importStatusLabel(item.import_status) }}
                  </span>
                </td>
                <td class="text-center align-middle">
                  <button
                    class="btn btn-icon btn-xs btn-outline-info"
                    title="Sửa"
                    @click="handleEdit(item)"
                  >
                    <i class="fas fa-pen-nib"></i>
                  </button>
                  <button
                    class="btn btn-icon btn-xs btn-outline-success ml-2"
                    title="Cập nhật trạng thái thanh toán"
                    @click="handlePaymentStatus(item)"
                  >
                    <i class="fas fa-money-check-alt"></i>
                  </button>
                  <button
                    class="btn btn-icon btn-xs btn-outline-danger ml-2"
                    title="Xóa"
                    @click="handleDelete(item)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            </template>

            <tr v-else>
              <td colspan="10" class="text-center">Chưa có dữ liệu</td>
            </tr>
          </tbody>
        </table>

        <div v-if="invoiceList.length > 0">
          <div class="d-flex justify-content-center align-items-center mt-4 mb-3">
            <paginate
              v-model="page"
              :page-count="lastPage"
              :page-range="3"
              :margin-pages="1"
              :click-handler="clickCallback"
              :prev-text="'Trước'"
              :next-text="'Sau'"
              :container-class="'pagination b-pagination'"
              :pageLinkClass="'page-link'"
              :next-link-class="'next-link-item'"
              :prev-link-class="'prev-link-item'"
              :prev-class="'page-link'"
              :next-class="'page-link'"
              :page-class="'page-item'"
            />
          </div>
          <div class="d-flex justify-content-end">
            <div>
              Tổng số bản ghi:
              <span class="ml-1 text-primary font-weight-bold">{{ total }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  GET_PRODUCTS,
  GET_ACCOUNTING_INVOICES,
  GET_ACCOUNTING_INVOICE,
  CREATE_ACCOUNTING_INVOICE,
  UPDATE_ACCOUNTING_INVOICE,
  DELETE_ACCOUNTING_INVOICE,
  EXPORT_ACCOUNTING_INVOICES,
} from "@/core/services/store/logistics/logistics.module";
import { exportExcel } from "@/core/filters";
import InvoiceFormModal from "./components/InvoiceFormModal.vue";
import InvoicePaymentStatusModal from "./components/InvoicePaymentStatusModal.vue";

const HEADER_ROW_EXPORT_INVOICES = [
  { key: "stt", value: "STT" },
  { key: "invoice_code", value: "Mã hóa đơn" },
  { key: "pnk_code", value: "Mã PNK" },
  { key: "product_names", value: "Sản phẩm" },
  { key: "total_amount", value: "Tổng tiền" },
  { key: "purchase_date", value: "Ngày mua" },
  { key: "created_at", value: "Ngày tạo" },
  { key: "payment_status_text", value: "Trạng thái thanh toán" },
  { key: "payment_code", value: "Mã thanh toán" },
  { key: "import_status_text", value: "Trạng thái nhập kho" },
];

const EXPORT_INVOICES_FILE_NAME = "Danh sách hóa đơn.xlsx";

export default {
  name: "InvoiceManagement",
  components: {
    InvoiceFormModal,
    InvoicePaymentStatusModal,
  },
  data() {
    return {
      page: 1,
      lastPage: 1,
      total: 0,
      loading: false,
      exporting: false,
      invoiceModalVisible: false,
      invoiceModalMode: "create",
      editingInvoice: null,
      needPurchaseProductOptions: [],
      productOptionsLoading: false,
      paymentStatusModalVisible: false,
      paymentStatusInvoice: null,
      invoiceList: [],
      filterTimeout: null,
      filterQuery: {
        invoice_code: "",
        pnk_code: "",
        product_name: "",
        purchase_date: "",
        created_date: "",
        payment_status: null,
        import_status: null,
      },
      paymentStatusOptions: [
        { value: 0, label: "Chưa thanh toán" },
        { value: 1, label: "Thanh toán 50%" },
        { value: 2, label: "Đã thanh toán" },
      ],
      importStatusOptions: [
        { value: 0, label: "Chưa nhập" },
        { value: 1, label: "Nhập một phần" },
        { value: 2, label: "Nhập toàn bộ" },
      ],
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Logistics", route: "" },
      { title: "Danh sách hóa đơn" },
    ]);
    this.getInvoiceList();
    this.getNeedPurchaseProducts();
  },
  methods: {
    createApiError(response, fallbackMessage) {
      const error = new Error(response?.message || fallbackMessage);
      error.data = response?.data;
      return error;
    },
    async getInvoiceList() {
      this.loading = true;
      try {
        const response = await this.$store.dispatch(GET_ACCOUNTING_INVOICES, {
          page: this.page,
          ...this.filterQuery,
        });
        if (response?.error) throw this.createApiError(response, "Không thể tải danh sách hóa đơn");

        this.invoiceList = response.data ?? [];
        this.page = response.pagination?.current_page ?? 1;
        this.lastPage = response.pagination?.last_page ?? 1;
        this.total = response.pagination?.total ?? this.invoiceList.length;
      } catch (error) {
        this.invoiceList = [];
        this.$message.error(error?.message || "Không thể tải danh sách hóa đơn");
      } finally {
        this.loading = false;
      }
    },
    async getNeedPurchaseProducts() {
      this.productOptionsLoading = true;
      try {
        const response = await this.$store.dispatch(GET_PRODUCTS, {
          page: 1,
          reorder_level: 1,
          status: 0
        });
        if (!response?.error) {
          this.needPurchaseProductOptions = response.data ?? [];
        }
      } catch (error) {
        console.error("Error get need-purchase products", error);
        this.needPurchaseProductOptions = [];
      } finally {
        this.productOptionsLoading = false;
      }
    },
    formatCurrency(value) {
      return `${new Intl.NumberFormat("vi-VN").format(Number(value) || 0)} đ`;
    },
    formatDate(value) {
      if (!value) return "-";
      const datePart = String(value).split(" ")[0];
      const parts = datePart.split("-");
      return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : value;
    },
    formatProductNames(products = []) {
      return products.map((item) => item.name || item.product_name).filter(Boolean).join(", ") || "-";
    },
    paymentStatusLabel(status) {
      const labels = {
        0: "Chưa thanh toán",
        1: "Thanh toán 50%",
        2: "Đã thanh toán",
      };
      return labels[Number(status)] || labels[0];
    },
    paymentStatusBadgeClass(status) {
      return {
        badge: true,
        "badge-warning": Number(status) === 0,
        "badge-info": Number(status) === 1,
        "badge-success": Number(status) === 2,
      };
    },
    importStatusLabel(status) {
      const labels = {
        0: "Chưa nhập",
        1: "Nhập một phần",
        2: "Nhập toàn bộ",
      };
      return labels[Number(status)] || labels[0];
    },
    importStatusBadgeClass(status) {
      return {
        badge: true,
        "badge-secondary": Number(status) === 0,
        "badge-warning": Number(status) === 1,
        "badge-success": Number(status) === 2,
      };
    },
    handleFilter() {
      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
        this.filterTimeout = null;
      }
      this.page = 1;
      this.getInvoiceList();
    },
    handleFilterInput() {
      if (this.filterTimeout) clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.handleFilter();
      }, 300);
    },
    handleAddNew() {
      this.invoiceModalMode = "create";
      this.editingInvoice = null;
      this.invoiceModalVisible = true;
    },
    async handleEdit(item) {
      this.loading = true;
      try {
        const response = await this.$store.dispatch(GET_ACCOUNTING_INVOICE, item.id);
        if (response?.error || !response?.data) {
          throw this.createApiError(response, "Không thể tải chi tiết hóa đơn");
        }
        this.invoiceModalMode = "edit";
        this.editingInvoice = response.data;
        this.invoiceModalVisible = true;
      } catch (error) {
        this.$message.error(error?.message || "Không thể tải chi tiết hóa đơn");
      } finally {
        this.loading = false;
      }
    },
    async handleInvoiceSubmit(payload) {
      const action = this.invoiceModalMode === "create"
        ? CREATE_ACCOUNTING_INVOICE
        : UPDATE_ACCOUNTING_INVOICE;
      const response = await this.$store.dispatch(action, payload);
      if (response?.error) throw this.createApiError(response, "Không thể lưu hóa đơn");
      return response;
    },
    onInvoiceSubmitSuccess() {
      this.$message.success(
        this.invoiceModalMode === "create"
          ? "Thêm hóa đơn thành công"
          : "Cập nhật hóa đơn thành công"
      );
      this.getInvoiceList();
    },
    handlePaymentStatus(item) {
      this.paymentStatusInvoice = { ...item };
      this.paymentStatusModalVisible = true;
    },
    async handlePaymentStatusSubmit(payload) {
      const detailResponse = await this.$store.dispatch(GET_ACCOUNTING_INVOICE, payload.id);
      if (detailResponse?.error || !detailResponse?.data) {
        throw this.createApiError(detailResponse, "Không thể tải chi tiết hóa đơn");
      }

      const response = await this.$store.dispatch(UPDATE_ACCOUNTING_INVOICE, {
        ...detailResponse.data,
        payment_status: payload.payment_status,
        payment_code: payload.payment_code,
      });
      if (response?.error) throw this.createApiError(response, "Không thể cập nhật trạng thái");
      return response;
    },
    onPaymentStatusSubmitSuccess() {
      this.$message.success("Cập nhật trạng thái thanh toán thành công");
      this.getInvoiceList();
    },
    async handleDelete(item) {
      try {
        await this.$confirm(`Xóa hóa đơn ${item.invoice_code}?`, "Xác nhận", {
          confirmButtonText: "Đồng ý",
          cancelButtonText: "Bỏ qua",
          type: "warning",
        });
        const response = await this.$store.dispatch(DELETE_ACCOUNTING_INVOICE, item.id);
        if (response?.error) throw this.createApiError(response, "Không thể xóa hóa đơn");
        this.$message.success("Xóa hóa đơn thành công");
        if (this.invoiceList.length === 1 && this.page > 1) this.page -= 1;
        this.getInvoiceList();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          this.$message.error(error?.message || "Không thể xóa hóa đơn");
        }
      }
    },
    clickCallback(page) {
      this.page = page;
      this.getInvoiceList();
    },
    async handleExport() {
      this.exporting = true;
      try {
        const response = await this.$store.dispatch(EXPORT_ACCOUNTING_INVOICES, this.filterQuery);
        if (response?.error) throw this.createApiError(response, "Không thể xuất dữ liệu");

        const dataExport = response.data ?? [];
        exportExcel(HEADER_ROW_EXPORT_INVOICES, dataExport, EXPORT_INVOICES_FILE_NAME, true);
        this.$message.success("Xuất dữ liệu thành công");
      } catch (error) {
        this.$message.error(error?.message || "Không thể xuất dữ liệu");
      } finally {
        this.exporting = false;
      }
    },
  },
  beforeDestroy() {
    if (this.filterTimeout) clearTimeout(this.filterTimeout);
  },
};
</script>

<style scoped>
.table-stt {
  width: 5%;
}

.table-actions {
  width: 145px;
}

.product-names {
  max-width: 260px;
}
</style>
